import { bindQueueRoute } from "./appControls/bridge"
import { Code, FlaskConical, ListTodo, Loader2, PanelRightOpen, Settings, Sparkles } from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Navigate, useParams } from "react-router"
import { TaskNavbarSlot } from "./layout/CodeAppLayout"
import { QueueModeInputControls } from "./components/sidebar/QueueModeInputControls"
import { QueueModeHandoffBanner, QueueModeHud } from "./components/sidebar/QueueModeHud"
import { codeSidebarManager } from "./components/sidebar/sidebarManager"
import { getNavigationTargetAfterTaskClose } from "./components/sidebar/taskCloseNavigation"
import { queueModeManager, type QueueModeHandledKind } from "./components/sidebar/queueModeManager"
import { sortSidebarTasks } from "./components/sidebar/taskSorting"
import { getLastViewed } from "./constants"
import { useMediaQuery } from "./hooks/useMediaQuery"
import { CodeLayout, type CodeLayoutProps } from "./layout/CodeLayout"
import { isCodeModuleAvailable } from "./nestAPIs/capabilities"
import { DiffLabPage } from "./pages/DiffLabPage"
import { OnboardingPage } from "./pages/OnboardingPage"
import { TaskCreateDraftsMenu, TaskCreatePage } from "./pages/TaskCreatePage"
import { TaskCreationPage } from "./pages/TaskCreationPage"
import { TaskPage } from "./pages/TaskPage"
import { WorkspaceCreatePage } from "./pages/WorkspaceCreatePage"
import { WorkspaceSettingsPage } from "./pages/WorkspaceSettingsPage"
import { currentPlatformKind } from "./platform/detect"
import type { PlatformKind } from "./platform/types"
import { useCodeNavigate } from "./routing"
import { useCodeStore } from "./store/context"

// Wrapper to inject isCodeModuleAvailable prop into CodeLayout
const Layout = (props: Omit<CodeLayoutProps, "isCodeModuleAvailable">) => <CodeLayout {...props} isCodeModuleAvailable={isCodeModuleAvailable()} />
const QUEUE_ADVANCE_DELAY_MS = 3_000

export function shouldAutoHideTaskNavbar(platform: PlatformKind, compactViewport: boolean, taskPaneDocked = false): boolean {
    return !taskPaneDocked && (platform !== "electron" || !compactViewport)
}

// ==================== Route: /dashboard/code ====================

interface RouteTaskSummary {
    id: string
    closed?: boolean
    createdAt: string
    updatedAt?: string
    lastActivityAt?: string
    lastEvent?: { at: string }
}

function getMostRecentTaskId(tasks: RouteTaskSummary[]): string | undefined {
    const zeroTime = new Date(0).toISOString()
    const openTasks = tasks.filter((t) => !t.closed)
    if (openTasks.length === 0) return undefined
    const sorted = openTasks.sort((a, b) => {
        const aTime = a.lastEvent?.at ?? a.lastActivityAt ?? a.updatedAt ?? a.createdAt ?? zeroTime
        const bTime = b.lastEvent?.at ?? b.lastActivityAt ?? b.updatedAt ?? b.createdAt ?? zeroTime
        return bTime.localeCompare(aTime)
    })
    return sorted[0]?.id
}

export const CodeBaseRoute = observer(() => {
    const codeStore = useCodeStore()
    const navigate = useCodeNavigate()

    // Wait for repos to load, then redirect
    if (codeStore.repos.reposLoading) {
        return (
            <Layout title="Code" icon={<Code size="1.25rem" className="text-muted" />}>
                <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Loader2 size="2rem" className="animate-spin mb-4 opacity-50" />
                    <div className="text-sm">Loading...</div>
                </div>
            </Layout>
        )
    }

    // No workspaces - check if onboarding needed
    const onboardingCompleted = codeStore.personalSettingsStore?.settings.current.onboardingCompleted
    if (codeStore.repos.repos.length === 0 && !onboardingCompleted) {
        return (
            <Layout title="Welcome" icon={<Sparkles size="1.25rem" className="text-primary" />}>
                <OnboardingPage />
            </Layout>
        )
    }

    // No workspaces but onboarding done - go to create
    if (codeStore.repos.repos.length === 0) {
        return <Navigate to={navigate.path("CodeWorkspaceCreate")} replace />
    }

    // Try to restore last viewed workspace/task
    const lastViewed = getLastViewed()
    if (lastViewed) {
        const workspace = codeStore.repos.repos.find((r) => r.id === lastViewed.workspaceId)
        if (workspace) {
            // Validate task exists if specified
            if (lastViewed.taskId) {
                const taskExists = codeStore.tasks.getSidebarTasksForRepo(workspace.id).some((task) => task.id === lastViewed.taskId)
                if (taskExists) {
                    return <Navigate to={navigate.path("CodeWorkspaceTask", { workspaceId: workspace.id, taskId: lastViewed.taskId })} replace />
                }
            }
            // Workspace exists but task doesn't - find most recent task
            const mostRecentTaskId = getMostRecentTaskId(codeStore.tasks.getSidebarTasksForRepo(workspace.id))
            if (mostRecentTaskId) {
                return <Navigate to={navigate.path("CodeWorkspaceTask", { workspaceId: workspace.id, taskId: mostRecentTaskId })} replace />
            }
            // Workspace exists but no tasks - go to task create
            return <Navigate to={navigate.path("CodeWorkspaceTaskCreate", { workspaceId: workspace.id })} replace />
        }
    }

    // Fallback: first workspace + most recent task
    const firstWorkspace = codeStore.repos.repos[0]
    const mostRecentTaskId = getMostRecentTaskId(codeStore.tasks.getSidebarTasksForRepo(firstWorkspace.id))
    if (mostRecentTaskId) {
        return <Navigate to={navigate.path("CodeWorkspaceTask", { workspaceId: firstWorkspace.id, taskId: mostRecentTaskId })} replace />
    }

    // First workspace, no tasks - go to task create
    return <Navigate to={navigate.path("CodeWorkspaceTaskCreate", { workspaceId: firstWorkspace.id })} replace />
})

// ==================== Route: /dashboard/code/workspace/create ====================

export const CodeWorkspaceCreateRoute = observer(() => {
    return (
        <Layout title="Add Workspace" icon={<Code size="1.25rem" className="text-muted" />}>
            <WorkspaceCreatePage />
        </Layout>
    )
})

// ==================== Route: /dashboard/code/workspace/:workspaceId ====================

export const CodeWorkspaceRoute = observer(() => {
    const { workspaceId } = useParams<{ workspaceId: string }>()
    const navigate = useCodeNavigate()

    if (!workspaceId) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    // Redirect to task create
    return <Navigate to={navigate.path("CodeWorkspaceTaskCreate", { workspaceId })} replace />
})

// ==================== Route: /dashboard/code/workspace/:workspaceId/settings ====================

export const CodeWorkspaceSettingsRoute = observer(() => {
    const codeStore = useCodeStore()
    const { workspaceId } = useParams<{ workspaceId: string }>()
    const navigate = useCodeNavigate()

    const repo = workspaceId ? codeStore.repos.getRepo(workspaceId) : null

    if (!workspaceId || !repo) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    return (
        <Layout title={`${repo.name} Settings`} icon={<Settings size="1.25rem" className="text-muted" />} workspaceId={workspaceId}>
            <WorkspaceSettingsPage workspaceId={workspaceId} repo={repo} />
        </Layout>
    )
})

// ==================== Route: /dashboard/code/workspace/:workspaceId/diff-lab ====================

export const CodeWorkspaceDiffLabRoute = observer(() => {
    const codeStore = useCodeStore()
    const { workspaceId } = useParams<{ workspaceId: string }>()
    const navigate = useCodeNavigate()
    const repo = workspaceId ? codeStore.repos.getRepo(workspaceId) : null

    if (!workspaceId || !repo) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    return (
        <Layout title={`${repo.name} Diff Lab`} icon={<FlaskConical size="1.25rem" className="text-primary" />} workspaceId={workspaceId}>
            <DiffLabPage workspaceId={workspaceId} repo={repo} />
        </Layout>
    )
})

// ==================== Route: /dashboard/code/workspace/:workspaceId/task/create ====================

export const CodeWorkspaceTaskCreateRoute = observer(() => {
    const codeStore = useCodeStore()
    const { workspaceId } = useParams<{ workspaceId: string }>()
    const navigate = useCodeNavigate()

    const repo = workspaceId ? codeStore.repos.getRepo(workspaceId) : null

    if (!workspaceId) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    // Workspace not found
    if (!repo) {
        return (
            <Layout workspaceId={workspaceId} title="New Task" icon={<Code size="1.25rem" className="text-muted" />}>
                <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Code size="3rem" className="mb-4 opacity-30" />
                    <div className="text-lg font-medium mb-2">Workspace not found</div>
                    <div className="text-sm">Select a workspace from the sidebar or add a new one</div>
                </div>
            </Layout>
        )
    }

    return (
        <Layout
            workspaceId={workspaceId}
            title={`New Task in ${repo.name}`}
            icon={<Code size="1.25rem" className="text-muted" />}
            navbarRight={<TaskCreateDraftsMenu workspaceId={workspaceId} />}
        >
            <TaskCreatePage workspaceId={workspaceId} repo={repo} />
        </Layout>
    )
})

// ==================== Route: /dashboard/code/workspace/:workspaceId/task/create/:creationId ====================

export const CodeWorkspaceTaskCreatingRoute = observer(() => {
    const codeStore = useCodeStore()
    const { workspaceId, creationId } = useParams<{ workspaceId: string; creationId: string }>()
    const navigate = useCodeNavigate()

    const repo = workspaceId ? codeStore.repos.getRepo(workspaceId) : null

    if (!workspaceId || !creationId) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    if (!repo) {
        return (
            <Layout workspaceId={workspaceId} title="Creating Task" icon={<Loader2 size="1.25rem" className="text-muted animate-spin" />}>
                <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Code size="3rem" className="mb-4 opacity-30" />
                    <div className="text-lg font-medium mb-2">Workspace not found</div>
                    <div className="text-sm">Select a workspace from the sidebar or add a new one</div>
                </div>
            </Layout>
        )
    }

    return (
        <Layout workspaceId={workspaceId} title="Creating Task" icon={<Loader2 size="1.25rem" className="text-muted animate-spin" />}>
            <TaskCreationPage workspaceId={workspaceId} creationId={creationId} />
        </Layout>
    )
})

// ==================== Route: /dashboard/code/workspace/:workspaceId/task/:taskId ====================

export const CodeWorkspaceTaskRoute = observer(() => {
    const codeStore = useCodeStore()
    const { workspaceId, taskId } = useParams<{ workspaceId: string; taskId: string }>()
    const navigate = useCodeNavigate()
    const compactViewport = useMediaQuery("(max-width: 720px)")
    const navbarAutoHideEnabled = shouldAutoHideTaskNavbar(currentPlatformKind(), compactViewport, codeSidebarManager.taskPaneDocked)

    const repo = workspaceId ? codeStore.repos.getRepo(workspaceId) : null
    const taskModel = taskId ? codeStore.tasks.getTaskModel(taskId) : null

    // Redirects
    if (!workspaceId || !taskId) {
        return <Navigate to={navigate.path("Code")} replace />
    }

    // Determine navbar title and icon
    const taskTitle = taskModel?.title || "Task"
    const isTaskClosed = taskModel?.isClosed ?? false
    const isTaskRunning = taskModel?.isWorking ?? false
    const [workspaceSidebarOpen, setWorkspaceSidebarOpen] = useState(false)
    const [taskNavbarVisible, setTaskNavbarVisible] = useState(true)
    const effectiveTaskNavbarVisible = !navbarAutoHideEnabled || taskNavbarVisible
    const queueAdvanceTimerRef = useRef<number | null>(null)
    const queueAdvanceSourceTaskIdRef = useRef<string | null>(null)
    const queueIsWaitingForCapacity = queueModeManager.isWaitingForCapacity
    const queueHasCapacity = queueModeManager.hasCapacity
    const queueCapacityWaitSourceTaskId = queueModeManager.capacityWaitSourceTaskId
    useEffect(() => {
        setWorkspaceSidebarOpen(false)
        setTaskNavbarVisible(true)
    }, [taskId])
    useEffect(
        () => () => {
            if (queueAdvanceTimerRef.current !== null) window.clearTimeout(queueAdvanceTimerRef.current)
            queueAdvanceTimerRef.current = null
            queueModeManager.clearAdvanceCountdown()
            queueModeManager.clearCapacityWait()
        },
        [taskId]
    )
    useEffect(() => {
        if (!taskModel?.exists) return
        queueModeManager.observeTask(taskId, !isTaskClosed && !isTaskRunning)
    }, [isTaskClosed, isTaskRunning, taskId, taskModel?.exists])
    useEffect(() => {
        if (!queueModeManager.isActive || !queueIsWaitingForCapacity || !queueHasCapacity) return

        const nextTask = queueModeManager.nextAfterTask(queueCapacityWaitSourceTaskId ?? "")
        queueModeManager.clearCapacityWait()
        if (!nextTask) return
        queueModeManager.completeHandoff(nextTask.taskId, queueCapacityWaitSourceTaskId ?? taskId)
        navigate.go("CodeWorkspaceTask", { workspaceId: nextTask.workspaceId, taskId: nextTask.taskId })
    }, [navigate, queueCapacityWaitSourceTaskId, queueHasCapacity, queueIsWaitingForCapacity, taskId])
    const navbarTitle = taskModel ? (
        <div className="flex items-center gap-1.5 min-w-0">
            {isTaskRunning && (
                <>
                    <Loader2 size="0.9rem" className="text-primary animate-spin flex-shrink-0" aria-hidden="true" />
                    <span className="sr-only">Running</span>
                </>
            )}
            {isTaskClosed && <span className="font-mono text-[11px] text-muted flex-shrink-0">[Closed]</span>}
            <span className="min-w-0 truncate font-medium text-base-content" title={taskTitle}>
                {taskTitle}
            </span>
        </div>
    ) : (
        taskTitle
    )
    const navbarIcon = <ListTodo size="1.25rem" className="text-muted" />
    const sleepNavigationTarget = getNavigationTargetAfterTaskClose(
        sortSidebarTasks(
            codeStore.tasks.getSidebarTasksForRepo(workspaceId).map((task) => ({ ...task, workspaceId })),
            {
                pinnedTaskIds: codeStore.personalSettingsStore?.settings.current.pinnedTaskIds,
                runningTaskIds: codeStore.runtimes.runningTaskIds,
            }
        ),
        taskId
    )
    const holdQueueAdvance = useCallback(() => {
        if (queueAdvanceTimerRef.current !== null) window.clearTimeout(queueAdvanceTimerRef.current)
        queueAdvanceTimerRef.current = null
        queueModeManager.clearAdvanceCountdown()
        queueModeManager.clearCapacityWait()
    }, [])
    const cancelQueueCountdownForInteraction = useCallback(() => {
        if (queueModeManager.advanceAtMs !== null) holdQueueAdvance()
    }, [holdQueueAdvance])
    useEffect(() => {
        // Capture before control handlers so an accepted command can start a fresh countdown.
        document.addEventListener("pointerdown", cancelQueueCountdownForInteraction, true)
        document.addEventListener("click", cancelQueueCountdownForInteraction, true)
        return () => {
            document.removeEventListener("pointerdown", cancelQueueCountdownForInteraction, true)
            document.removeEventListener("click", cancelQueueCountdownForInteraction, true)
        }
    }, [cancelQueueCountdownForInteraction])
    const scheduleQueueAdvance = useCallback(
        (kind: QueueModeHandledKind): boolean => {
            if (!queueModeManager.isActive) return false
            if (queueAdvanceSourceTaskIdRef.current === taskId) return true

            queueModeManager.trackTask(taskId)
            queueAdvanceSourceTaskIdRef.current = taskId
            queueModeManager.recordHandledTask(taskId, kind)
            queueMicrotask(() => {
                if (queueAdvanceSourceTaskIdRef.current === taskId) queueAdvanceSourceTaskIdRef.current = null
            })

            if (queueModeManager.nextAfterTask(taskId) === null) {
                if (queueAdvanceTimerRef.current !== null) window.clearTimeout(queueAdvanceTimerRef.current)
                queueAdvanceTimerRef.current = null
                queueModeManager.clearAdvanceCountdown()
                return false
            }

            if (queueAdvanceTimerRef.current !== null) window.clearTimeout(queueAdvanceTimerRef.current)
            const scheduledRunId = queueModeManager.runId
            const scheduledAdvanceAtMs = queueModeManager.beginAdvanceCountdown(QUEUE_ADVANCE_DELAY_MS)
            if (scheduledAdvanceAtMs === null) return false

            queueAdvanceTimerRef.current = window.setTimeout(() => {
                queueAdvanceTimerRef.current = null
                if (!queueModeManager.isActive || queueModeManager.runId !== scheduledRunId || queueModeManager.advanceAtMs !== scheduledAdvanceAtMs) {
                    return
                }

                queueModeManager.clearAdvanceCountdown()
                const nextTask = queueModeManager.nextAfterTask(taskId)
                if (!nextTask) return
                if (!queueModeManager.hasCapacity) {
                    queueModeManager.waitForCapacity(taskId)
                    return
                }
                queueModeManager.completeHandoff(nextTask.taskId)
                navigate.go("CodeWorkspaceTask", { workspaceId: nextTask.workspaceId, taskId: nextTask.taskId })
            }, QUEUE_ADVANCE_DELAY_MS)

            return true
        },
        [navigate, taskId]
    )
    const continueQueue = useCallback(() => {
        if (!queueModeManager.isActive) return
        holdQueueAdvance()
        const nextTask = queueModeManager.nextAfterTask(taskId)
        if (!nextTask) return
        if (!queueModeManager.hasCapacity) {
            queueModeManager.waitForCapacity(taskId)
            return
        }
        queueModeManager.completeHandoff(nextTask.taskId, taskId)
        navigate.go("CodeWorkspaceTask", { workspaceId: nextTask.workspaceId, taskId: nextTask.taskId })
    }, [holdQueueAdvance, navigate, taskId])
    const toggleCurrentTaskMajor = useCallback(() => {
        void codeStore.tasks
            .setTaskQueuePriority(taskId, taskModel?.queuePriority === "major" ? undefined : "major")
            .catch((error) => console.error("[Routes] Failed to update task Q priority:", error))
    }, [codeStore.tasks, taskId, taskModel?.queuePriority])
    const skipQueueTask = useCallback(
        async (minutes: number) => {
            if (!queueModeManager.isActive || !queueModeManager.isTaskEligible(taskId)) return
            holdQueueAdvance()
            await codeStore.tasks.sleepTask(taskId, new Date(Date.now() + minutes * 60_000).toISOString())
            queueModeManager.notifyTaskSkipped()

            const nextTask = queueModeManager.nextAfterTask(taskId)
            if (!nextTask) return
            if (!queueModeManager.hasCapacity) {
                queueModeManager.waitForCapacity(taskId)
                return
            }
            queueModeManager.trackTask(nextTask.taskId)
            navigate.go("CodeWorkspaceTask", { workspaceId: nextTask.workspaceId, taskId: nextTask.taskId })
        },
        [codeStore.tasks, holdQueueAdvance, navigate, taskId]
    )
    const handleWorkDispatched = useCallback(() => scheduleQueueAdvance("work"), [scheduleQueueAdvance])
    useEffect(
        () => bindQueueRoute({ taskId, hold: holdQueueAdvance, next: continueQueue, skip: skipQueueTask, accepted: handleWorkDispatched }),
        [taskId, holdQueueAdvance, continueQueue, skipQueueTask, handleWorkDispatched]
    )
    const navigateAfterSleep = () => {
        if (queueModeManager.isActive) {
            scheduleQueueAdvance("snooze")
            return
        }
        if (sleepNavigationTarget?.type === "task") {
            navigate.go("CodeWorkspaceTask", { workspaceId: sleepNavigationTarget.workspaceId, taskId: sleepNavigationTarget.taskId })
            return
        }
        navigate.go("CodeWorkspaceTaskCreate", { workspaceId: sleepNavigationTarget?.workspaceId ?? workspaceId })
    }
    const navbarRight =
        taskModel && taskId ? (
            <button
                type="button"
                className="btn flex h-9 w-9 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content min-[721px]:hidden"
                onClick={() => {
                    setWorkspaceSidebarOpen((open) => !open)
                }}
                title="Task tools"
                aria-label="Open task tools"
                aria-pressed={workspaceSidebarOpen}
            >
                <PanelRightOpen size={16} />
            </button>
        ) : undefined

    // Workspace not found
    if (!repo) {
        return (
            <Layout workspaceId={workspaceId} taskId={taskId} title={navbarTitle} icon={navbarIcon} navbarRight={navbarRight}>
                <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Code size="3rem" className="mb-4 opacity-30" />
                    <div className="text-lg font-medium mb-2">Workspace not found</div>
                    <div className="text-sm">Select a workspace from the sidebar or add a new one</div>
                </div>
            </Layout>
        )
    }

    // Task not loaded yet
    if (!taskModel?.exists) {
        return (
            <Layout workspaceId={workspaceId} taskId={taskId} title={navbarTitle} icon={navbarIcon} navbarRight={navbarRight}>
                <div className="flex flex-col items-center justify-center h-full text-muted">
                    <Loader2 size="2rem" className="animate-spin mb-4 opacity-50" />
                    <div className="text-sm">Loading task...</div>
                </div>
            </Layout>
        )
    }

    return (
        <Layout
            workspaceId={workspaceId}
            taskId={taskId}
            title={navbarTitle}
            icon={navbarIcon}
            navbarRight={navbarRight}
            navbarOverlay
            navbarVisible={effectiveTaskNavbarVisible}
        >
            <TaskPage
                workspaceId={workspaceId}
                taskId={taskId}
                taskModel={taskModel}
                onSlept={navigateAfterSleep}
                onComposerInput={cancelQueueCountdownForInteraction}
                onStopRequested={holdQueueAdvance}
                onWorkDispatchStarted={holdQueueAdvance}
                onWorkDispatched={handleWorkDispatched}
                workspaceSidebarOpen={workspaceSidebarOpen}
                onCloseWorkspaceSidebar={() => setWorkspaceSidebarOpen(false)}
                onNavbarVisibilityChange={navbarAutoHideEnabled ? setTaskNavbarVisible : undefined}
                queueControl={
                    queueModeManager.isActive ? (
                        <QueueModeHud title={<TaskNavbarSlot inline />} isMajor={taskModel?.queuePriority === "major"} onToggleMajor={toggleCurrentTaskMajor} />
                    ) : undefined
                }
                queueInputControl={
                    queueModeManager.isActive ? <QueueModeInputControls taskId={taskId} onSkipFor={skipQueueTask} onContinue={continueQueue} /> : undefined
                }
                queueHandoff={
                    queueModeManager.isActive &&
                    (queueModeManager.advanceAtMs !== null || queueModeManager.isWaitingForCapacity || queueModeManager.isBrowsingUnavailableTask) ? (
                        <QueueModeHandoffBanner onContinue={continueQueue} />
                    ) : undefined
                }
            />
        </Layout>
    )
})
